import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { GetServerSidePropsContext } from 'next';
import { CONTENT_TYPE_VALUE, TIME_SET_CONTENT } from 'src/constants';
import { IResponse, IPageData, IContentDetail } from 'src/interface';
import { ArrayUtils, ObjectUtils } from 'src/utils';
import { useViewTime } from 'src/hooks';
import { trackingPageDetail } from 'src/utils/tracking';
import { setIdsRead } from 'src/utils/content';
import PageService from 'src/services/PageService';
import { ContentDispatchActions } from 'src/redux/actions';
import { strUserIdSelector } from 'src/selectors/auth';
import { Element, IconButton, LazyLoad, RouterLink } from 'src/components';
import { GATracking } from 'src/modules/shared/components/GA';
import {
    ContentMain,
    ContentSocial,
    ContentSource,
    ContentsHistory,
    ContentsRelated,
    EventSoccer,
} from 'src/modules/content/views';

import styles from './style.module.scss';

export function ContentDetail({
    resp,
    isBot = false,
}: {
    resp: IResponse<IPageData<IData>>;
    isBot: boolean;
}) {
    const dispatch = useDispatch();
    const data = resp?.data || ({} as any);
    const { related = [], content = {} as any } = data;
    const strUserId = useSelector(strUserIdSelector);
    const router = useRouter();
    const intervalRef: MutableRefObject<any> = useRef(1);

    const iframeRef = useRef<any>(null);
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        if (content.contentId && strUserId) {
            trackingPageDetail(content.contentId, strUserId);
        }
    }, [content.contentId, strUserId]);

    useEffect(() => {
        if (intervalRef && intervalRef.current) {
            intervalRef.current = setInterval(() => {
                setIdsRead(content.contentId);
                clearInterval(intervalRef.current);
                dispatch(ContentDispatchActions.setReadId(content.contentId));
            }, TIME_SET_CONTENT);
        }
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [content]);

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }
        const setHeightIframe = (e: any) => {
            const eventName = e.data[0];
            var data = e.data[1];

            switch (eventName) {
                case 'setHeight':
                    setHeight(data);
                    break;
            }
        };
        const layout = document.getElementById('layout');
        layout?.classList.add(styles['embed-layout']);
        window.addEventListener('message', setHeightIframe, false);
        return () => {
            window.removeEventListener('message', setHeightIframe, false);
        };
    }, [iframeRef]);

    useEffect(() => {
        if (ObjectUtils.isEmpty(content)) {
            router.replace('/');
        }
    }, []);

    const { ref } = useViewTime({
        tracking: 'page',
        contentId: content.contentId,
        threshold: 0,
        onFinish: () => {},
    });

    if (ObjectUtils.isEmpty(content)) {
        return null;
    }

    return (
        <Element domRef={ref}>
            <GATracking content={content} />
            <>
                {content.embedUrl ? (
                    <iframe
                        ref={iframeRef}
                        id="ifrademe"
                        style={{
                            display: 'block',
                            position: 'relative',
                            width: 'calc(100% + 32px)',
                            marginLeft: '-16px',
                            marginTop: '-16px',
                            height:
                                height === 0 ? 'calc(100vh - 90px)' : height,
                        }}
                        src={content.embedUrl}
                        title=""
                        frameBorder="0"
                    />
                ) : (
                    <>
                        <Element
                            className={cn(styles['content-wrapper'], {
                                [styles['is-live']]:
                                    content?.contentTypes?.includes(
                                        CONTENT_TYPE_VALUE.LIVE,
                                    ),
                            })}
                        >
                            <ContentMain {...content} lazyLoad={!isBot} />
                            <ContentsHistory content={content} />
                            <ContentSource
                                publisher={content.publisher}
                                originalUrl={content.originalUrl}
                                redirectUrl={content.redirectUrl}
                                tags={content.topTags}
                            />
                            <EventSoccer content={content} />
                            <ContentSocial
                                content={content}
                                url={content.url}
                            />
                        </Element>
                        {ArrayUtils.isEmpty(related) ? (
                            <LazyLoad>
                                <ContentsRelated
                                    contentId={content.contentId as any}
                                />
                            </LazyLoad>
                        ) : (
                            <ContentsRelated
                                contentId={content.contentId as any}
                                related={related}
                                isBot={true}
                            />
                        )}

                        <RouterLink to="/" className={cn(styles['btn-back'])}>
                            <IconButton
                                title="Về trang chủ"
                                text="Về trang chủ"
                                icon="home"
                            ></IconButton>
                        </RouterLink>
                    </>
                )}
            </>
        </Element>
    );
}

interface IData extends IContentDetail {
    description: string;
    originalUrl?: string;
    bodys: any[];
    speechs: any[];
}

export async function getServerSideProps(
    ctx: GetServerSidePropsContext,
): Promise<any> {
    return PageService.get(ctx, 'content-detail', { id: ctx?.params?.id });
}

export default ContentDetail;
